import {
    AuthContext,
    DataTable,
    DataTableCell,
    DataTablePresenter,
    IAuthContextProps,
    IFilterSchema,
    ITableColumn,
    ITableFetchOptions,
    MailDetail,
    ProviderMail,
    ProviderMailSortType,
    Tooltip,
} from '@enter/core';
import { mdiCheckCircleOutline } from '@mdi/js';
import { Icon } from '@mdi/react';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { Row } from 'react-table';
import { DialogContext, IDialogContextProps } from 'services';
import { IInjectedRootStore, IObservedRootStore, parseSortBy } from 'stores';
import * as S from './inbox.styles';

interface IMailProps extends IInjectedRootStore {
    authContext: IAuthContextProps;
    dialogContext: IDialogContextProps;
    mails?: ProviderMail[];
    hideControls?: boolean;
    hideShipmentInformation?: boolean;
}

interface IMailState {
    loading: boolean;
    options: ITableFetchOptions | undefined;
}

const columns: ITableColumn<ProviderMail>[] = [
    {
        id: 'shipment',
        accessor: 'mailCartId',
        disableSortBy: true,
        maxWidth: 32,
        Cell: ({ cell }) => (
            <S.TableIconCell>
                {!!cell.value && (
                    <Tooltip direction="top" title={<span>Included in the shipment</span>}>
                        <Icon path={mdiCheckCircleOutline} size="18px" />
                    </Tooltip>
                )}
            </S.TableIconCell>
        ),
    },
    {
        id: 'received',
        Header: 'Received Date',
        accessor: 'receivedDate',
        sortType: ProviderMailSortType.RECEIVED,
        Cell: DataTableCell('DateTime'),
    },
    {
        id: 'scanned',
        Header: 'Scanned Date',
        accessor: 'scanDate',
        sortType: ProviderMailSortType.SCANNED,
        Cell: DataTableCell('DateTime'),
    },
    {
        id: 'pages',
        Header: 'Pages',
        accessor: 'pages',
        sortType: ProviderMailSortType.PAGES,
    },
    {
        id: 'weight',
        Header: 'Weight',
        accessor: 'weight',
        Cell: ({ cell }): string => (cell.value ? `${cell.value} oz.` : ''),
        disableSortBy: true,
    },
    {
        id: 'barcode',
        Header: 'Barcode',
        accessor: 'barcode',
        sortType: ProviderMailSortType.BARCODE,
        defaultSort: { id: 'barcode', desc: true },
    },
];
const filterSchemas: IFilterSchema<string>[] = [
    {
        label: 'Claim ID',
        filterId: 'filterClaimId',
        columnId: 'claimId',
        filterType: 'String',
    },
    {
        label: 'Document Id',
        filterId: 'filterDocumentId',
        columnId: 'documentId',
        filterType: 'String',
    },
    {
        label: 'Created Date',
        filterId: 'filterReceived',
        columnId: 'received',
        filterType: 'Date',
    },
    {
        label: 'Received Date',
        filterId: 'filterReceived',
        columnId: 'received',
        filterType: 'Date',
    },
    {
        label: 'Pages',
        filterId: 'filterPages',
        columnId: 'pages',
        filterType: 'Number',
    },
];

class MailPage extends React.Component<IMailProps, IMailState> {
    private presenter: DataTablePresenter<ProviderMail>;

    constructor(props: IMailProps) {
        super(props);
        this.state = {
            loading: false,
            options: undefined,
        };
        this.presenter = new DataTablePresenter<ProviderMail>({
            name: 'mail',
            data: props.mails || [],
            columns,
            filterSchemas,
        });
    }

    public componentDidMount(): void {
        document.title = 'ENTER Platform - Mail';
    }
    public componentWillUnmount(): void {
        this.authContext.cancelGroup('mails');
    }

    public render(): React.ReactElement {
        return (
            <DataTable
                presenter={this.presenter}
                isLoading={this.state.loading}
                onFetch={this.onFetch}
                onRowClick={this.onRowClick}
                renderRowSubComponent={this.renderRowSubComponent}
                hideNavigation={this.props.hideControls}
                hideFiltering={this.props.hideControls}
                hidePagination={this.props.hideControls}
                hideSettings={this.props.hideControls}
                disableSorting={this.props.hideControls}
            />
        );
    }

    private get authContext(): IAuthContextProps {
        return this.props.authContext;
    }

    private get dialogContext(): IDialogContextProps {
        return this.props.dialogContext;
    }

    private onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
        if (this.props.mails) {
            return;
        }

        if (!_.isEqual(this.state.options, fetchOptions)) {
            try {
                this.setState({
                    options: fetchOptions,
                    loading: true,
                });
                this.authContext.cancelGroup('mails');
                this.authContext.setGroup('mails');

                const { pageIndex, pageSize, sortBy, validFilters } = fetchOptions;
                const sortAndOrder = parseSortBy(sortBy, columns);
                const mailListing = await this.props.rootStore.claimStore.loadMailListing({
                    page: pageIndex,
                    pageSize,
                    ...sortAndOrder,
                    filters: validFilters,
                });
                this.presenter.onDataUpdate(mailListing.items || []);
                this.presenter.onDataTotalChange(mailListing.total || 0);
                this.setState({
                    loading: false,
                });
            } catch (err) {
                this.dialogContext.showError(err);
            }
        }
    };

    private onRowClick = async (row: Row<ProviderMail>): Promise<void> => {
        // retrieve mail cart if necessary
        row.toggleRowExpanded();
    };

    private renderRowSubComponent = (row: Row<ProviderMail>): React.ReactElement => {
        const { claimStore } = this.props.rootStore;
        return (
            <S.Expand>
                <MailDetail
                    mail={row.original}
                    appType="provider"
                    downloadDocument={claimStore.loadDocumentDownloadUrl.bind(claimStore)}
                    loadMailCart={claimStore.loadMailCart.bind(claimStore)}
                    hideShipmentInformation={this.props.hideShipmentInformation}
                />
            </S.Expand>
        );
    };
}

export const InboxMail: React.FC<
    IObservedRootStore & { mails?: ProviderMail[]; hideShipmentInformation?: boolean }
> = inject('rootStore')(
    observer(
        (
            props: IObservedRootStore & { mails?: ProviderMail[]; hideShipmentInformation?: boolean },
        ): React.ReactElement => {
            const { rootStore, mails, hideShipmentInformation } = props;
            if (!rootStore) {
                return <></>;
            }
            const authContext = useContext(AuthContext);
            const dialogContext = useContext(DialogContext);

            return (
                <MailPage
                    authContext={authContext}
                    dialogContext={dialogContext}
                    rootStore={rootStore}
                    mails={mails}
                    hideShipmentInformation={hideShipmentInformation}
                    hideControls={!!mails}
                />
            );
        },
    ),
);
